/** The shapes `GridTable` takes and the table power features share.
 *
 * They live here, apart from the component, so `useGridPower` (state) and `GridTable` (drawing) can
 * both name a column without importing each other.
 */

import type { ReactNode } from "react";

export interface GridSummable<T> {
  /** The row's number as a decimal string (`"184000.0000000000"`), or null when it has none. Summed
   * exactly (`decimal.ts`), never as a float. */
  value: (row: T) => string | null | undefined;
  /** Writes a sum with the product's own formatter. */
  format: (sum: string) => ReactNode;
}

export interface GridColumn<T> {
  id: string;
  /** Header text. A string: it is also the accessible name of the sort button. */
  header: string;
  /** The header is read by a screen reader and not drawn - for a column of row menus, which has a name
   * ("Actions") and no room to print it. */
  headerHidden?: boolean;
  cell: (row: T, index: number) => ReactNode;
  /** A number column: right-aligned, tabular figures. Same as `align: "right"`. */
  numeric?: boolean;
  align?: "left" | "right" | "center";
  /** The column's budget, in px (`160px`). A table with at least one column that states a width is laid out
   * on **fixed** tracks (`table-layout: fixed`): a column that states one gets exactly it, and the columns
   * that state none (one, usually the one that names the row) share what is left, never less than
   * `minWidth`. The table therefore fits its card; it scrolls, with a fade at the right edge, only when the
   * budgets plus the flexible columns' minimums are wider than the card. A table that states no width is
   * laid out by its content, as before. */
  width?: string;
  /** For a flexible column (one with no `width`) in a fixed-track table: the narrowest it is drawn, in px.
   * Default 160px. */
  minWidth?: string;
  /** The column holds sentences, so its cells may wrap (to about 56 characters a line). Default: one line,
   * cut with an ellipsis and the whole text as the cell's title. */
  wrap?: boolean;
  sortable?: boolean;
  /** The cell that names the row is a row header (`th scope="row"`), so a screen reader reading a
   * cell says whose row it is in. At most one column per table. */
  rowHeader?: boolean;
  /** The cell as plain text: what Export writes, what a group is called, what "filter this column"
   * compares. Without it the export falls back to a string or number `cell` and otherwise leaves the
   * cell empty - a figure is never exported as "[object Object]". */
  value?: (row: T) => string | number | null | undefined;
  /** Whether the column may be hidden. Default true, except the first column, which names the row. */
  hideable?: boolean;
  /** Whether rows may be grouped by this column. Default: whether `value` is given. */
  groupable?: boolean;
  /** Marks a numeric column as one a group may add up. Absent means no subtotal for this column. */
  summable?: GridSummable<T>;
  /** The id of the filter this column's menu opens ("Filter this column"). Defaults to the column id. */
  filterId?: string;
}

export interface GridTotals {
  /** The word in the row-number/first cell: "Total". */
  label?: string;
  /** Column id to the server's string for that column. A column with no entry is blank. */
  values: Readonly<Record<string, ReactNode>>;
}

export type GridDensity = "compact" | "comfortable" | "spacious";

/** Row heights in px, as the research settled them: 28, 36 and 48. */
export const DENSITY_HEIGHT: Readonly<Record<GridDensity, number>> = {
  compact: 28,
  comfortable: 36,
  spacious: 48,
};

export const DENSITIES: readonly GridDensity[] = ["compact", "comfortable", "spacious"];
